import devApiDefaultHost from './devApiDefaultHost.json';

/**
 * Fallback API hostname when **`REACT_APP_PROXY_TARGET`** / **`REACT_APP_API_BASE`** is unset.
 * Prefer DNS (`dev.offizz.ai`) — do not hardcode a VM IP. Override via env for other stacks.
 * **`src/setupProxy.js`** reads the same file.
 */
const DEFAULT_API_HOST = devApiDefaultHost.defaultApiHost;

/** If **`REACT_APP_PROXY_TARGET`** is set without **`http[s]://`** (e.g. hostname only), assume **HTTPS**. */
function ensureApiOriginForEnv(raw: string): string {
  const t = raw.trim();
  if (/^https?:\/\//i.test(t)) {
    return t;
  }
  return `https://${t.replace(/^\/+/, '')}`;
}

/** Login/auth targets are often plain **`http://localhost:PORT`** in dev. */
function ensureLoginOriginForEnv(raw: string): string {
  const t = raw.trim();
  if (/^https?:\/\//i.test(t)) {
    return t;
  }
  const host = t.replace(/^\/+/, '');
  if (/^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(host)) {
    return `http://${host}`;
  }
  return `https://${host}`;
}

/** Build **`https[s]://host[:port][/optional/prefix]`** without a trailing slash (except root). */
function publicApiBaseUrl(normalizedEnvUrl: string): { readonly origin: string; readonly hostname: string } {
  const url = new URL(normalizedEnvUrl);
  let p = url.pathname.replace(/\/+$/, '');
  if (p === '/') {
    p = '';
  }
  const origin = p ? `${url.origin}${p}` : url.origin;
  return { origin, hostname: url.hostname };
}

function readLoginProxyTargetEnv(): string | null {
  const raw =
    process.env.REACT_APP_LOGIN_PROXY_TARGET?.trim() || process.env.LOGIN_PROXY_TARGET?.trim() || null;
  return raw || null;
}

/** Build-time API base — `REACT_APP_API_BASE` (backend docs) or `REACT_APP_PROXY_TARGET` (legacy). */
function readApiTargetEnv(): string | null {
  return (
    process.env.REACT_APP_API_BASE?.trim() ||
    process.env.REACT_APP_PROXY_TARGET?.trim() ||
    null
  );
}

/** Optional path prefix from env URL (e.g. `/dev-api`) for same-origin production deploys. */
function apiPathPrefixFromEnvUrl(envOrigin: string): string {
  try {
    const pathname = new URL(envOrigin).pathname.replace(/\/+$/, '');
    if (pathname && pathname !== '/') {
      return pathname;
    }
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * Resolved API origin for **`BASE_URL_DIRECT`** and hostname for display / tooling.
 * Set **`REACT_APP_API_BASE`** or **`REACT_APP_PROXY_TARGET`** (e.g. `https://dev.offizz.ai/dev-api`).
 */
function resolveApiOriginAndHost(): { readonly origin: string; readonly hostname: string } {
  const rawInput = readApiTargetEnv();
  if (rawInput) {
    try {
      const normalized = ensureApiOriginForEnv(rawInput);
      return publicApiBaseUrl(normalized);
    } catch {
      /* fall through */
    }
  }
  return {
    origin: `https://${DEFAULT_API_HOST}`,
    hostname: DEFAULT_API_HOST,
  };
}

/**
 * Auth/login/register/refresh origin — **`REACT_APP_LOGIN_PROXY_TARGET`** when set (e.g. local auth on :3000).
 * Default: same as main API (`devApiDefaultHost.json` / `REACT_APP_PROXY_TARGET`).
 */
function resolveLoginOriginAndHost(): { readonly origin: string; readonly hostname: string } {
  const rawInput = readLoginProxyTargetEnv();
  if (rawInput) {
    try {
      const normalized = ensureLoginOriginForEnv(rawInput);
      return publicApiBaseUrl(normalized);
    } catch {
      /* fall through */
    }
  }
  return resolveApiOriginAndHost();
}

const resolved = resolveApiOriginAndHost();
const loginResolved = resolveLoginOriginAndHost();

/**
 * Production: when **`REACT_APP_USE_SAME_ORIGIN=true`**, API calls use the browser host
 * so HTTPS UI and HTTPS API stay aligned. A path prefix from env (e.g. `/dev-api`) is kept.
 */
function applySameOriginInBrowser(envOrigin: string): string {
  if (
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_USE_SAME_ORIGIN === 'true' &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) {
    const prefix = apiPathPrefixFromEnvUrl(envOrigin);
    return `${window.location.origin.replace(/\/+$/, '')}${prefix}`;
  }
  return envOrigin;
}

/** Hostname of **`BASE_URL_DIRECT`** (derived from **`REACT_APP_PROXY_TARGET`** or default). */
export const BASE_URL_HOST = resolved.hostname;

/** Direct **`https://…`** origin (production, Jest, and browser direct-retry URL in dev). */
export const BASE_URL_DIRECT = applySameOriginInBrowser(resolved.origin);

/** Hostname of the login/auth service (`REACT_APP_LOGIN_PROXY_TARGET` or same as main API). */
export const LOGIN_BASE_URL_HOST = loginResolved.hostname;

/** Direct login/auth origin (cloud API by default; local only when split env is set). */
export const LOGIN_BASE_URL_DIRECT = applySameOriginInBrowser(loginResolved.origin);

/** True when login/auth uses a different upstream than the main API (typical local dev split). */
export const USE_SPLIT_LOGIN_UPSTREAM =
  LOGIN_BASE_URL_DIRECT.replace(/\/+$/, '') !== BASE_URL_DIRECT.replace(/\/+$/, '');

const useDevProxy =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_API_DIRECT !== 'true';

export const BASE_URL = useDevProxy ? '/dev-api-proxy' : BASE_URL_DIRECT;

/** Same-origin **`/dev-api-proxy`** in dev when unified; **`/dev-login-proxy`** only when split upstream is configured. */
export const LOGIN_BASE_URL = useDevProxy
  ? USE_SPLIT_LOGIN_UPSTREAM
    ? '/dev-login-proxy'
    : '/dev-api-proxy'
  : LOGIN_BASE_URL_DIRECT;

function joinBaseAndPath(base: string, apiPath: string): string {
  const b = base.replace(/\/+$/, '');
  const tail = apiPath.replace(/^\/+/, '');
  if (tail === '') {
    return `${b}/`;
  }
  return `${b}/${tail}`;
}

/**
 * Builds the full browser URL/path for an API route (assets, bookings, profile, …).
 */
export function resolveApiPath(apiPath: string): string {
  return joinBaseAndPath(BASE_URL, apiPath);
}

/** `https://…` API URL — for dev fallback when `/dev-api-proxy` fails. */
export function resolveApiPathDirect(apiPath: string): string {
  return joinBaseAndPath(BASE_URL_DIRECT, apiPath);
}

/** Login, register, and `/auth/refresh` — same dev proxy as assets unless **`REACT_APP_LOGIN_PROXY_TARGET`** is set. */
export function resolveLoginApiPath(apiPath: string): string {
  return joinBaseAndPath(LOGIN_BASE_URL, apiPath);
}

export function resolveLoginApiPathDirect(apiPath: string): string {
  return joinBaseAndPath(LOGIN_BASE_URL_DIRECT, apiPath);
}

/** Dev app uses same-origin `/dev-api-proxy`; production uses `BASE_URL_DIRECT` already. */
export function isUsingRelativeDevProxy(): boolean {
  return BASE_URL.startsWith('/');
}

/** True when local `npm start` targets the VM dev stack (`/dev-api`, ports 3100–3110). */
export function isLocalDevApiStack(): boolean {
  return (
    process.env.NODE_ENV === 'development' &&
    isUsingRelativeDevProxy() &&
    /\/dev-api\/?$/i.test(BASE_URL_DIRECT.replace(/\/+$/, ''))
  );
}

export function isUsingRelativeLoginDevProxy(): boolean {
  return LOGIN_BASE_URL.startsWith('/');
}
